import React, { forwardRef, useLayoutEffect, useRef, useState } from "react";
import { Animation } from "shared/utils/animation.utils";
import styles from "./DropDownAnimation.module.scss";

const getHeights = (element: HTMLDivElement): [number, number] => {
  const currentHeight = element.getBoundingClientRect().height;
  element.style.height = "auto";
  const maxHeight = element.getBoundingClientRect().height;
  return [currentHeight, maxHeight];
};

interface IDropDownAnimation {
  isOpen: boolean;
  duration: number;
  callback?: (event: ICallback) => void;
  onProcessing?: (event: ICallback) => void;
}

interface ICallback {
  element: HTMLDivElement | null;
  act: "open" | "close";
  type: "start" | "processing" | "end";
  completeness: number;
}

export const DropDownAnimation = forwardRef<HTMLDivElement, IDropDownAnimation & React.HTMLProps<HTMLDivElement>>(
  ({ isOpen, duration, callback, onProcessing, className, children }, ref: React.Ref<HTMLDivElement>) => {
    const animationRef = useRef<Animation>(new Animation());
    const animation = animationRef.current;
    const div = useRef<HTMLDivElement>(null);
    const init = useRef(true);
    const isDom = useRef(isOpen);
    const [update, setUpdate] = useState(true);
    if (isOpen) isDom.current = true;

    const declareRef = (refDiv: HTMLDivElement | null) => {
      (div as React.MutableRefObject<HTMLDivElement | null>).current = refDiv;
      if (typeof ref === "function") {
        ref(refDiv);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = refDiv;
      }
    };

    const open = () => {
      const animationDiv = div.current!;
      const [, targetHeight] = getHeights(animationDiv);

      animationDiv.classList.add(styles.overflowYHidden);
      /*!animation.current.animationProcessing &&*/
      callback &&
        callback({
          element: animationDiv,
          act: "open",
          type: "start",
          completeness: 0,
        });

      animation.initAnimate({
        duration: duration,
        timing: "easyInOut",
        draw: (progress) => {
          onProcessing &&
            onProcessing({
              element: animationDiv,
              act: "open",
              type: "processing",
              completeness: progress,
            });
          if (progress === 1) {
            animationDiv.style.height = "auto";
            animationDiv.classList.remove(styles.overflowYHidden);
            callback &&
              callback({
                element: animationDiv,
                act: "open",
                type: "end",
                completeness: progress,
              });
            return;
          }
          animationDiv.style.height = targetHeight * progress + "px";
        },
      });
    };

    const close = () => {
      const animationDiv = div.current!;
      const [, maxHeight] = getHeights(animationDiv);

      animationDiv.classList.add(styles.overflowYHidden);
      !animation.animationProcessing &&
        callback &&
        callback({
          element: animationDiv,
          act: "close",
          type: "start",
          completeness: 0,
        });

      animation.initAnimate({
        duration: duration,
        timing: "easyInOut",
        draw: (progress) => {
          onProcessing &&
            onProcessing({
              element: animationDiv,
              act: "close",
              type: "processing",
              completeness: progress,
            });
          if (animationDiv) {
            animationDiv.style.height = maxHeight * (1 - progress) + "px";
          }
          if (progress === 1) {
            callback &&
              callback({
                element: animationDiv ? animationDiv : null,
                act: "close",
                type: "end",
                completeness: progress,
              });
            isDom.current = false;
            setUpdate(!update);
          }
        },
      });
    };

    useLayoutEffect(() => {
      if (init.current || !isDom.current) {
        return;
      }
      if (isOpen) {
        open();
      } else {
        close();
      }
    }, [isOpen]);

    useLayoutEffect(() => {
      init.current = false;
    }, []);

    return isDom.current ? (
      <div ref={declareRef} className={className}>
        {children}
      </div>
    ) : null;
  }
);

DropDownAnimation.displayName = "DropDownAnimation";
